/**
 * Pure layout logic for {@link BulkActionBar}. Kept in its own module so the
 * component file only exports the component (React Fast Refresh), and so the
 * collapse maths can be unit-tested without a DOM.
 *
 * The bar shows everything the measured width allows, in three stages:
 *  - Stage 1 (step 0)              — every action on the bar, labelled. No "…".
 *  - Stage 2 (steps 1 … L)         — still labelled; `step` actions folded into
 *                                    the "…" menu in collapse-rank order (Delete
 *                                    leaves first). L = actionCount - 3, so this
 *                                    stage never drops below three labelled
 *                                    actions beside the count + Clear.
 *  - Stage 3 (steps L+1 … L+1+N)   — last resort: the bar switches to icons
 *                                    (tooltips + aria-labels) and re-expands to
 *                                    every action, then folds them into "…" one
 *                                    at a time again as the width keeps shrinking.
 *
 * Visible actions always keep their bar order, so an action is either in its
 * place or in "…" — it never appears somewhere new, and it is never clipped.
 */

/** Breathing room (px) the bar keeps on each side before it counts as "doesn't fit". */
export const FIT_MARGIN = 32;

/**
 * Fallback breakpoint for environments that report no layout (SSR / jsdom, where
 * offsetWidth is 0): >= it → labelled, below → icons. A real browser always
 * measures, so it never reaches this.
 */
export const MOBILE_FALLBACK_BREAKPOINT = 768;

/**
 * Labelled actions the bar keeps beside the count + Clear before icons-only
 * becomes the only way to fit — i.e. Signature, Share and Download.
 */
export const MIN_LABELLED_ACTIONS = 3;

/**
 * Collapse ranks — the order actions leave the bar for the "…" menu, lowest
 * first: Delete, then Ask Franklin, then the context actions (right to left),
 * then the document actions (right to left). Signature/Share/Download cling to
 * the bar longest, so they are the three that survive into stage 3. A hosting
 * page's own action (the Brief page's "Add appendix") is the reason that page
 * offers a selection at all, so it outlasts every built-in one.
 */
export const COLLAPSE_RANK = {
  delete: 1,
  askFranklin: 2,
  diffOverview: 3,
  compare: 4,
  compareDrawings: 5,
  comments: 6,
  addNewVersion: 7,
  viewDrawing: 8,
  verify: 9,
  rename: 10,
  movecopy: 11,
  download: 12,
  share: 13,
  signature: 14,
  hostAction: 15,
} as const;

/** The subset of a bar action this module needs to decide what collapses. */
export interface CollapsibleAction {
  key: string;
  collapseRank: number;
}

/** Last labelled step — below it the bar would show fewer than three labels. */
export const labelledFloor = (actionCount: number) =>
  Math.max(0, actionCount - MIN_LABELLED_ACTIONS);

/** Highest step: the labelled stage, then one icon step per action folded into "…". */
export const maxStep = (actionCount: number) => labelledFloor(actionCount) + 1 + actionCount;

/** True once the bar has dropped to icons (stage 3). */
export const isCompactStep = (step: number, actionCount: number) =>
  step > labelledFloor(actionCount);

/**
 * How many actions sit in "…" at `step`. Entering stage 3 re-expands the bar
 * (icons are narrow enough to show more), then collapses again one at a time.
 */
export function hiddenCountForStep(step: number, actionCount: number): number {
  const floor = labelledFloor(actionCount);
  const hidden = step <= floor ? step : step - floor - 1;
  return Math.min(Math.max(hidden, 0), actionCount);
}

/** Keys folded into the "…" menu at `step`, lowest collapse rank first. */
export function hiddenKeysForStep(actions: CollapsibleAction[], step: number): Set<string> {
  const hideCount = hiddenCountForStep(step, actions.length);
  if (hideCount === 0) return new Set();
  return new Set(
    [...actions]
      .sort((a, b) => a.collapseRank - b.collapseRank)
      .slice(0, hideCount)
      .map((a) => a.key)
  );
}

/** Step to assume when the DOM reports no width (SSR / jsdom). */
export const fallbackStep = (viewportWidth: number, actionCount: number) =>
  viewportWidth < MOBILE_FALLBACK_BREAKPOINT ? labelledFloor(actionCount) + 1 : 0;

/**
 * One monotonic collapse step from a single measurement. The bar only ever
 * shrinks here — expanding back is driven by the caller resetting the step when
 * the viewport grows or the action set changes, which keeps the measure/collapse
 * loop from oscillating between two modes that each "fit" by the other's maths.
 */
export function nextStep(
  step: number,
  measuredWidth: number,
  viewportWidth: number,
  actionCount: number
): number {
  // No layout info (SSR / jsdom): fall back to the viewport breakpoint.
  if (measuredWidth === 0) return Math.max(step, fallbackStep(viewportWidth, actionCount));
  if (measuredWidth <= viewportWidth - FIT_MARGIN) return step;
  return Math.min(step + 1, maxStep(actionCount));
}
